import { useMemo } from 'react';
import type { AccommodationListingDTO, Filters } from '@shared/types/Accommodations.types';
import { distanceInMiles } from '@shared/utils/distanceInMiles';
import { useAccommodationsSortOrder } from './useAccommodationsSortOrder';
import { sortByFavorite, sortByHostStayingHere, sortByOrderList } from '@shared/utils/array';
import { useListingsSortQueryParam } from './useAccommodationsConsoleSearchParamsState/useListingsSortQueryParam';
import type { LatLngLiteral } from '@shared/core/google-maps/types';
import { SortOption } from '@shared/constants/accommodations';

type Bucket = 'available' | 'unpriced' | 'soldOut';

type UseListingSortArgs = Readonly<{
  defaultSortOption: SortOption;
  listings: ReadonlyArray<AccommodationListingDTO>;
  filters?: Filters;
  eventHandle?: Maybe<string>;
  sortAnchor?: Maybe<{ name: string; location: Maybe<LatLngLiteral> }>;
  /** Couple's total fund balance, injected by the cash-fund scenario for the HIGHEST_FUND_SAVINGS sort. */
  fundBalance?: number;
}>;

export const useListingSort = ({ listings, defaultSortOption, sortAnchor, filters, eventHandle, fundBalance }: UseListingSortArgs) => {
  const { currentSort, updateSort } = useListingsSortQueryParam(defaultSortOption);

  const isCouplesPicksRelevant = currentSort === SortOption.COUPLES_PICKS || defaultSortOption === SortOption.COUPLES_PICKS;
  const { pageContainerBySlug } = useAccommodationsSortOrder({ eventHandle, skip: !isCouplesPicksRelevant });

  const sortedListings = useMemo(() => {
    let sorted: ReadonlyArray<AccommodationListingDTO>;

    switch (currentSort) {
      case SortOption.TOP_MATCHES:
        sorted = [...listings].sort((a, b) => (b.searchScore ?? 0) - (a.searchScore ?? 0));
        break;
      case SortOption.COUPLES_PICKS: {
        const blockOrdering = pageContainerBySlug?.blockOrdering;
        const coupleListings = listings.filter(listing => listing.isPickedByCouple);
        const nonCoupleListings = listings.filter(listing => !listing.isPickedByCouple);
        const sortedCoupleListings = sortByFavorite(sortByHostStayingHere(sortByOrderList(coupleListings, blockOrdering ?? [])));
        sorted = [...sortedCoupleListings, ...nonCoupleListings];
        break;
      }

      case SortOption.PRICE_LOW_TO_HIGH:
        sorted = [...listings].sort((a, b) => {
          const aPrice = a.pricingInfo?.values?.nightlyBaseRate ?? Number.MAX_VALUE;
          const bPrice = b.pricingInfo?.values?.nightlyBaseRate ?? Number.MAX_VALUE;
          return aPrice - bPrice;
        });
        break;

      case SortOption.PRICE_HIGH_TO_LOW:
        sorted = [...listings].sort((a, b) => {
          const aPrice = a.pricingInfo?.values?.nightlyBaseRate ?? -1;
          const bPrice = b.pricingInfo?.values?.nightlyBaseRate ?? -1;
          return bPrice - aPrice;
        });
        break;

      case SortOption.MOST_SAVINGS:
        sorted = [...listings].sort((a, b) => {
          const aDiscount = a.pricingInfo?.values?.discountPercentage ?? 0;
          const bDiscount = b.pricingInfo?.values?.discountPercentage ?? 0;
          return bDiscount - aDiscount;
        });
        break;

      case SortOption.HIGHEST_FUND_SAVINGS: {
        // Highest fund savings = lowest out-of-pocket (the fund covers the most). Sort by the "+$"
        // out-of-pocket (max(0, stayTotal − balance)) ascending, tie-broken by discount % (the same
        // "Most Savings" logic). Listings with no stay total (room blocks/custom, no fund) sort last.
        // TODO(TRAV-3348 follow-up): use the real per-listing fund-savings BE field.
        const balance = fundBalance ?? 0;
        sorted = [...listings].sort((a, b) => {
          const aTotal = a.pricingInfo?.values?.totalRate;
          const bTotal = b.pricingInfo?.values?.totalRate;
          const aOutOfPocket = aTotal == null ? Number.MAX_VALUE : Math.max(0, aTotal - balance);
          const bOutOfPocket = bTotal == null ? Number.MAX_VALUE : Math.max(0, bTotal - balance);
          if (aOutOfPocket !== bOutOfPocket) {
            return aOutOfPocket - bOutOfPocket;
          }
          const aDiscount = a.pricingInfo?.values?.discountPercentage ?? 0;
          const bDiscount = b.pricingInfo?.values?.discountPercentage ?? 0;
          return bDiscount - aDiscount;
        });
        break;
      }

      case SortOption.DISTANCE:
        sorted = [...listings].sort((a, b) => {
          if (sortAnchor?.location) {
            const aDistance = distanceInMiles(sortAnchor.location, a.location) ?? Number.MAX_VALUE;
            const bDistance = distanceInMiles(sortAnchor.location, b.location) ?? Number.MAX_VALUE;
            return aDistance - bDistance;
          }
          return 0;
        });
        break;

      default:
        sorted = listings;
    }

    // When showAvailableOnly is true, push sold-out hotels to the end (and unpriced hotels near the end)
    if (filters?.showAvailableOnly) {
      const buckets: Readonly<Record<Bucket, AccommodationListingDTO[]>> = {
        available: [],
        unpriced: [],
        soldOut: [],
      };

      for (const listing of sorted) {
        let bucket: Bucket = 'available';

        if (listing.type === 'standardHotel' && !listing.isSponsored) {
          if (listing.isSoldOutForSelectedDates) {
            bucket = 'soldOut';
          } else if (!listing.pricingInfo.isLoading && !listing.pricingInfo.values) {
            bucket = 'unpriced';
          }
        }

        buckets[bucket].push(listing);
      }

      return [...buckets.available, ...buckets.unpriced, ...buckets.soldOut];
    }

    return sorted;
  }, [listings, pageContainerBySlug?.blockOrdering, currentSort, sortAnchor, filters?.showAvailableOnly, fundBalance]);

  return {
    sortedListings,
    sortOption: currentSort,
    updateSort,
  };
};
